import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar
 * @property {HTMLButtonElement} addToCartButton
 * @property {HTMLElement} quantityDisplay
 * @property {HTMLElement} quantityNumber
 * @property {HTMLImageElement} [productImage]
 */

/**
 * Sticky Buy Now component.
 *
 * Shows a sticky Buy Now bar when the main product purchase buttons
 * scroll out of view.
 *
 * Clicking the sticky button triggers Shopify's native
 * accelerated checkout / Buy It Now button.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = [
    'stickyBar',
    'addToCartButton',
    'quantityDisplay',
    'quantityNumber'
  ];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {boolean} */
  #hiddenByBottom = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;

    const target = this.closest('.shopify-section');

    target?.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect,
      { signal }
    );

    document.addEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#handleQuantityUpdate,
      { signal }
    );

    this.#getInitialQuantity();

    /*
     * Hide the sticky bar when Shopify Chat is active.
     */
    customElements.whenDefined('shopify-chat').then(() => {
      if (signal.aborted) return;

      if (this.#isStuck && this.#isChatActive()) {
        this.#hideStickyBar();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();

    this.#abortController.abort();
  }

  /**
   * Sets up IntersectionObservers to control sticky bar visibility.
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();

    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');

    if (!buyButtonsBlock) return;

    /*
     * Find footer.
     */
    const footer =
      document.querySelector('footer') ??
      document.querySelector('[class*="footer-group"]');

    if (!footer) return;

    /*
     * Observe the main Buy Buttons section.
     */
    this.#buyButtonsIntersectionObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;

        if (!entry) return;

        /*
         * Show sticky bar when the main purchase buttons
         * have scrolled above the viewport.
         */
        if (!entry.isIntersecting && !this.#isStuck) {
          const rect = entry.target.getBoundingClientRect();

          if (rect.bottom < 0 || rect.top < 0) {
            if (this.#isChatActive()) return;

            this.#showStickyBar();
          }
        }

        /*
         * Hide sticky bar when the main purchase buttons
         * come back into view.
         */
        else if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = false;
          this.#hideStickyBar();
        }
      }
    );

    /*
     * Observe footer.
     *
     * This prevents the sticky button from covering the footer.
     */
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;

        if (!entry) return;

        /*
         * Footer visible.
         */
        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        }

        /*
         * Footer no longer visible.
         */
        else if (!entry.isIntersecting && this.#hiddenByBottom) {
          const rect = buyButtonsBlock.getBoundingClientRect();

          /*
           * Only show sticky bar if the main Buy Now buttons
           * are above the viewport.
           */
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;

            if (!this.#isChatActive()) {
              this.#showStickyBar();
            }
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px'
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
  }

  /**
   * Handles sticky Buy Now button click.
   *
   * Finds Shopify's existing native Buy It Now button
   * and triggers it.
   */
  handleAddToCartClick = () => {
    /*
     * Find the Shopify accelerated checkout button
     * inside the current product section.
     */
    const section = this.closest('.shopify-section');

    if (!section) {
      console.warn(
        '[sticky-add-to-cart] Product section not found.'
      );

      return;
    }

    /*
     * Shopify's native Buy It Now button.
     */
    const buyNowButton = section.querySelector(
      '.shopify-payment-button__button--unbranded'
    );

    if (!buyNowButton) {
      console.warn(
        '[sticky-add-to-cart] Shopify Buy It Now button not found.'
      );

      return;
    }

    /*
     * Make sure the button is actually usable.
     */
    if (
      buyNowButton instanceof HTMLButtonElement &&
      buyNowButton.disabled
    ) {
      return;
    }

    /*
     * Trigger Shopify's native accelerated checkout.
     */
    buyNowButton.click();
  };

  /**
   * Handles product variant changes.
   *
   * @param {ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (
      !(event.target instanceof Element) ||
      event.target.closest('product-card')
    ) {
      return;
    }

    /*
     * Get selected option/variant ID.
     */
    const { optionValueId } = event.detail ?? {};

    if (optionValueId) {
      this.dataset.currentVariantId = optionValueId;
    }

    /*
     * Wait for Shopify's variant update.
     */
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const {
          html,
          productId,
          resource: variant
        } = detail;

        /*
         * Make sure this update belongs to this product.
         */
        if (
          productId &&
          productId !== this.dataset.productId
        ) {
          return;
        }

        /*
         * Get updated sticky component.
         */
        const newStickyAddToCart =
          /** @type {HTMLElement | null} */
          (html.querySelector('sticky-add-to-cart'));

        if (!newStickyAddToCart) return;

        /*
         * Get updated sticky bar HTML.
         */
        const newStickyBar =
          newStickyAddToCart.querySelector(
            '[ref="stickyBar"]'
          );

        if (!newStickyBar) return;

        /*
         * Remember whether sticky bar is currently visible.
         */
        const currentStuck =
          this.refs.stickyBar.getAttribute('data-stuck') ||
          'false';

        /*
         * Check variant availability.
         */
        const variantAvailable =
          newStickyAddToCart.dataset.variantAvailable;

        /*
         * Replace sticky bar contents.
         */
        morph(
          this.refs.stickyBar,
          newStickyBar,
          {
            childrenOnly: true
          }
        );

        /*
         * Restore visibility.
         */
        this.refs.stickyBar.setAttribute(
          'data-stuck',
          currentStuck
        );

        this.dataset.variantAvailable =
          variantAvailable;

        /*
         * Update variant ID.
         */
        if (variant?.id) {
          this.dataset.currentVariantId =
            variant.id;
        }

        /*
         * Update button text.
         */
        this.#updateButtonText();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[sticky-add-to-cart] Variant update failed:',
            error
          );
        }
      });
  };

  /**
   * Handles quantity changes.
   *
   * @param {QuantitySelectorUpdateEvent} event
   */
  #handleQuantityUpdate = (event) => {
    /*
     * Ignore cart drawer quantity changes.
     */
    if (event.detail.cartLine) return;

    this.#currentQuantity =
      event.detail.quantity;

    this.#updateButtonText();
  };

  /**
   * Shows sticky bar.
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;

    this.#isStuck = true;

    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides sticky bar.
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;

    this.#isStuck = false;

    stickyBar.dataset.stuck = 'false';
  }

  /**
   * Checks whether Shopify Chat is active.
   *
   * @returns {boolean}
   */
  #isChatActive() {
    if (!customElements.get('shopify-chat')) {
      return false;
    }

    return Boolean(
      document.querySelector('shopify-chat')
    );
  }

  /**
   * Gets product form.
   *
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId =
      this.dataset.productId;

    if (!productId) return null;

    const sectionElement =
      this.closest('.shopify-section');

    if (!sectionElement) return null;

    const sectionId =
      sectionElement.id.replace(
        'shopify-section-',
        ''
      );

    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets initial quantity.
   */
  #getInitialQuantity() {
    this.#currentQuantity =
      parseInt(
        this.dataset.initialQuantity || '1'
      ) || 1;

    this.#updateButtonText();
  }

  /**
   * Updates quantity display.
   */
  #updateButtonText() {
    const {
      addToCartButton,
      quantityDisplay,
      quantityNumber
    } = this.refs;

    /*
     * The sticky button is now Buy Now,
     * so we don't need to show quantity
     * on it.
     *
     * Keep this logic only for compatibility
     * with the existing theme structure.
     */
    quantityNumber.textContent =
      this.#currentQuantity.toString();

    quantityDisplay.style.display = 'none';

    /*
     * Make sure sticky button remains enabled
     * when the variant is available.
     */
    if (
      addToCartButton &&
      this.dataset.variantAvailable === 'false'
    ) {
      addToCartButton.disabled = true;
    } else if (addToCartButton) {
      addToCartButton.disabled = false;
    }
  }
}

if (
  !customElements.get('sticky-add-to-cart')
) {
  customElements.define(
    'sticky-add-to-cart',
    StickyAddToCartComponent
  );
}